import { ILog } from './logging.models';
import {
  LogBatcher,
  StorageKey,
  StorableLogs,
  LogBatchAndKey,
} from './log-batcher.util';
import { LocalTokens } from './local-tokens.enum';
import { environment } from 'src/environments/environment';

interface IAcceptedStorage {
  setItem(key: StorageKey, value: StorableLogs): void;
  getItem(key: StorageKey): string | null;
  removeItem(key: StorageKey): void;
}

interface ILogPersistorConfig {
  maxLogs: number;
  batchSize: number;
}

export interface ILogPersistor {
  save(logs: ILog[]): void;
  load(): ILog[];
  clear(): void;
}

/*
  This class was based on work in a prior Mitel project to optimize the process of both
  reading and writing stringified JSON logs (assuming string storage).

  We reduce the performance hit of serialization
  and deserialization by splitting the logs into multiple batches
  (meaning we parse 500 logs at a time, rather than say, 5000 logs).

  Each batch has its own storage key.
  We also maintain the log count separately from the batches to avoid a need to ever have to combine them all at once to check the length.
*/
export class LogPersistor implements ILogPersistor {
  private storage: IAcceptedStorage;
  private config: ILogPersistorConfig;
  private storageKeys: StorageKey[];

  constructor(
    storage: IAcceptedStorage,
    config: ILogPersistorConfig = {
      maxLogs: environment.maxLogs,
      batchSize: 100,
    }
  ) {
    this.storage = storage;
    this.config = config;
    this.storageKeys = this.generateStorageKeys();
  }

  // Public interface is just 'save', 'load' and 'clear' simple for consumer

  save = (logs: ILog[]): void => {
    return this._save(logs);
  };

  load = (): ILog[] => {
    return this._load();
  };

  clear = (): void => {
    this._clear();
  };

  // Private implementation details get to the optimization logic.

  private _save = (logs: ILog[]): void => {
    try {
      const currentBatchCount: number = this.loadCurrentBatchCount();
      const logBatches: ILog[][] = LogBatcher.splitLogsIntoBatches(
        logs,
        currentBatchCount,
        this.config.batchSize
      );

      logBatches.forEach((logBatch: ILog[]) => {
        const totalLogCount = this.loadCount();
        const updatedLogCount: number = totalLogCount + logBatch.length;
        const shouldRotate = updatedLogCount > this.config.maxLogs;
        const saveFn = shouldRotate
          ? this.rotateStoredBatches
          : this.saveToCurrentStoredBatch;
        return saveFn(logBatch, totalLogCount);
      });
    } catch (error) {
      this.handleFailedSave(error, logs);
    }
  };

  private _load = (): ILog[] => {
    const logs: ILog[] = LogBatcher.combineBatches(
      this.loadBatches(this.storageKeys)
    );
    if (logs && logs.length) {
      return logs;
    } else {
      return [];
    }
  };

  private saveToCurrentStoredBatch = (
    newLogs: ILog[],
    totalLogCount: number
  ): void => {
    const batchNumber: number = LogBatcher.calcCurrentBatchNumber(
      totalLogCount,
      this.config.batchSize
    );
    const storageKey: StorageKey = this.storageKeys[batchNumber];
    const storedBatch: ILog[] = this.loadBatch(storageKey);
    const updatedBatch: string = JSON.stringify([...storedBatch, ...newLogs]);
    this.saveBatch([storageKey, updatedBatch]);
    this.saveCount(totalLogCount + newLogs.length);
    this.saveCurrentBatchCount(storedBatch.length + newLogs.length);
  };

  private rotateStoredBatches = (
    newLogs: ILog[],
    totalLogCount: number
  ): void => {
    const storedBatches: LogBatchAndKey[] = this.loadBatches(this.storageKeys);
    const rotatedLogs: LogBatchAndKey[] = LogBatcher.rotateBatches(
      storedBatches,
      newLogs
    );
    const rotatedLogCount: number =
      totalLogCount + newLogs.length - this.getBatchCount(storedBatches[0]);
    this.saveBatches(rotatedLogs);
    this.saveCount(rotatedLogCount);
    this.saveCurrentBatchCount(newLogs.length);
  };

  private _clear = () => {
    this.removeItems(this.storageKeys);
    this.storage.removeItem(LocalTokens.TOTAL_LOGS);
    this.storage.removeItem(LocalTokens.BATCH_COUNT);
  };

  // The key names are dependent on how many log batches we have.
  private generateStorageKeys = (): StorageKey[] => {
    const keys: StorageKey[] = [];
    const keyCount: number = Math.ceil(
      this.config.maxLogs / this.config.batchSize
    );
    for (let i = 0; i < keyCount; i++) {
      keys.push(LocalTokens.LOG_BATCH + i);
    }
    return keys;
  };

  private getBatchCount = (batch: LogBatchAndKey): number => {
    return JSON.parse(batch[1]).length;
  };

  private loadBatches = (storageKeys: StorageKey[]): LogBatchAndKey[] => {
    const batches: LogBatchAndKey[] = [];
    storageKeys.forEach((key) => {
      const batch: string | null = this.storage.getItem(key);
      if (batch) {
        batches.push([key, batch]);
      }
    });
    return batches;
  };

  private saveBatches = (batches: LogBatchAndKey[]): void => {
    batches.forEach(this.saveBatch);
  };

  private loadBatch = (key: string): ILog[] | [] => {
    const storedLogs = this.storage.getItem(key);
    if (storedLogs && storedLogs.length) {
      return JSON.parse(storedLogs);
    } else {
      return [];
    }
  };

  private saveBatch = (batch: LogBatchAndKey): void => {
    return this.storage.setItem(batch[0], batch[1]);
  };

  private loadCount = (): number => {
    const count = Number(this.storage.getItem(LocalTokens.TOTAL_LOGS)) || 0;
    return count;
  };

  private saveCount = (newCount: number | string): void => {
    return this.storage.setItem(
      LocalTokens.TOTAL_LOGS,
      newCount ? newCount.toString() : ''
    );
  };

  private loadCurrentBatchCount = (): number => {
    return Number(this.storage.getItem(LocalTokens.BATCH_COUNT)) || 0;
  };

  private saveCurrentBatchCount = (newCount: number): void => {
    return this.storage.setItem(
      LocalTokens.BATCH_COUNT,
      newCount ? newCount.toString() : ''
    );
  };

  private removeItems = (storageKeys: StorageKey[]): void => {
    return storageKeys.forEach((item) => {
      this.storage.removeItem(item);
    });
  };

  private handleFailedSave = (error: Error, logs: ILog[]): void => {
    /*
      The main error case would be hitting browser storage limits. This is a challenging case because
      browser storage limits are dynamic. To ensure we don't compromise app functionality,
      the safest option I am seeing is to drop the current saved logs and start afresh.

      TODO: May wish to consider using File API to increase log storage, or revisit indexed DB as a storage mechanism.
      See https://github.com/mitel-networks/meet-web/issues/856
    */
    const logCount = this.loadCount();

    if (logCount) {
      this.clear();
      this._save(logs);
    } else {
      // Worst case scenario, no log count but we still failed to save. That means clearing logs will have no impact.
      // re-throw error so it can be caught by persistence cycle and handled.
      throw error;
    }
  };
}
